// BrandContext — runtime brand-variant chrome provider (CHG-1267).
//
// Implements the contract from specs/_product/dashboard-v2-brand-variants.md
// (CHG-1263 design doc). Closes the forward-promise from the CHG-1167 stub
// at contexts/BrandContext.tsx (which is now a re-export shim into this file).
//
// Architecture:
//   1. <BrandProvider> resolves the active variant via withHostnameOverlay()
//      on mount + on activeOrg change.
//   2. useEffect side effects set:
//        - documentElement.dataset.brand          (drives :root[data-brand=...] CSS overrides)
//        - documentElement.style[--ag-brand-*]    (per-variable inline override)
//        - document.title                          (display_name prefix)
//        - <link rel="icon"> href                  (brand favicon)
//   3. useBrand() consumers read the chrome + bridging brand state.
//
// Sidebar already reads useBrand().chrome.wordmark via the old contexts/
// stub — that read now resolves to the real BRAND_CHROME table. No
// shell/ modification required.
//
// Per the brief's "shell/ + pages immutable" constraint: all chrome
// integration flows through useBrand() reads (already in place on
// Sidebar) + CSS variable overrides (any surface using --color-primary,
// --color-bg-subtle, etc. picks up the brand-specific palette).

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  detectBrand,
  deriveApiBase,
  deriveWidgetBase,
  isV2StagingHost,
  type Brand,
} from "../lib/runtime-base-urls";
import { BRAND_CHROME, type BrandChrome, type BrandVariantId } from "./brandChrome";
import { withHostnameOverlay, type ActiveOrgLike } from "./withHostnameOverlay";

export interface BrandContextValue {
  /** CHG-1263 spec variant_id ("_default" | "mohseen" | "alorachurch"). */
  variantId: BrandVariantId;
  /** Runtime-base-urls Brand enum (kept for backward-compat consumers). */
  brand: Brand;
  /** Full chrome bundle for the active variant. */
  chrome: BrandChrome;
  /** Derived API + widget bases (delegated to runtime-base-urls). */
  apiBase: string;
  widgetBase: string;
  isV2Staging: boolean;
}

const defaultChrome = BRAND_CHROME._default;

const BrandContext = createContext<BrandContextValue>({
  variantId: "_default",
  brand: "aloragiving",
  chrome: defaultChrome,
  apiBase: "",
  widgetBase: "",
  isV2Staging: false,
});

export interface BrandProviderProps {
  children: ReactNode;
  /** Optional active org for brand_variant fallback per CHG-1263 §2.2 tier 2. */
  activeOrg?: ActiveOrgLike | null;
  /** Test override — bypasses hostname detection. */
  forceVariantId?: BrandVariantId;
}

export function BrandProvider({ children, activeOrg = null, forceVariantId }: BrandProviderProps) {
  const value = useMemo<BrandContextValue>(() => {
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const variantId: BrandVariantId =
      forceVariantId ?? withHostnameOverlay(hostname, activeOrg);
    const chrome = BRAND_CHROME[variantId];
    const brand = detectBrand(hostname);
    return {
      variantId,
      brand,
      chrome,
      apiBase: deriveApiBase(hostname),
      widgetBase: deriveWidgetBase(hostname),
      isV2Staging: isV2StagingHost(hostname),
    };
  }, [activeOrg, forceVariantId]);

  // Side effects: data-brand attribute + CSS variable overrides + document
  // title prefix + favicon href. Fires on mount + whenever variantId changes.
  useEffect(() => {
    if (typeof document === "undefined") return;
    applyBrandChrome(value.variantId, value.chrome);
  }, [value.variantId, value.chrome]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandContextValue {
  return useContext(BrandContext);
}

/** Re-exported for consumers that need the chrome type. */
export type { BrandChrome, BrandVariantId } from "./brandChrome";
export { BRAND_CHROME } from "./brandChrome";
export { withHostnameOverlay } from "./withHostnameOverlay";

// ─── Side-effect helper ────────────────────────────────────────────────────

/** Apply the brand chrome to documentElement + document.head. Exported for
 *  parity-testing + manual invocation; in production this fires from the
 *  BrandProvider useEffect above. */
export function applyBrandChrome(variantId: BrandVariantId, chrome: BrandChrome): void {
  const root = document.documentElement;
  // 1. data-brand attribute drives :root[data-brand=...] CSS override blocks.
  root.dataset.brand = variantId;
  // 2. Per-variable inline override (highest specificity; works even if
  //    tokens.css hasn't been updated yet OR if a later CHG adds new tokens).
  root.style.setProperty("--ag-brand-wordmark", `"${chrome.wordmark}"`);
  root.style.setProperty("--ag-brand-logo-url", `url("${chrome.logo_url}")`);
  root.style.setProperty("--ag-brand-favicon-url", `url("${chrome.favicon_url}")`);
  root.style.setProperty("--ag-brand-sidebar-accent", chrome.sidebar_accent_color);
  root.style.setProperty("--ag-brand-login-bg", chrome.login_bg_color);
  root.style.setProperty("--ag-brand-tagline", `"${chrome.tagline}"`);
  root.style.setProperty("--ag-brand-display-name", `"${chrome.display_name}"`);
  // Also override --color-primary so existing consumers (Sidebar header)
  // pick up the brand's sidebar accent without modifying shell/.
  root.style.setProperty("--color-primary", chrome.sidebar_accent_color);
  // 3. <title> prefix — append the display_name if not already present.
  const titlePrefix = `${chrome.display_name} · `;
  if (!document.title.startsWith(titlePrefix)) {
    const suffix = document.title.replace(/^[^·]+·\s*/, "");
    document.title = `${titlePrefix}${suffix || "Dashboard"}`;
  }
  // 4. <link rel="icon"> href.
  let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }
  if (favicon.href !== chrome.favicon_url) {
    favicon.href = chrome.favicon_url;
  }
}
